import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { Annotation } from '../../../types/bi';
import styles from './QueryDesigner.module.css';

interface AnnotationEditorProps {
  value: Annotation[];
  onChange: (annotations: Annotation[]) => void;
}

const COLOURS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#6b7280'];

export default function AnnotationEditor({ value, onChange }: AnnotationEditorProps) {
  const add = useCallback(() => {
    onChange([...value, { type: 'line', axis: 'y', value: 0, label: '', colour: '#ef4444' }]);
  }, [value, onChange]);

  const update = useCallback(
    (idx: number, patch: Partial<Annotation>) => {
      onChange(value.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
    },
    [value, onChange],
  );

  const remove = useCallback(
    (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    },
    [value, onChange],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {value.map((a, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
            padding: '0.5rem',
            border: '1px solid var(--border)',
            borderRadius: 6,
          }}
        >
          <div className={styles.row}>
            <select
              className={styles.selectSm}
              value={a.type}
              onChange={(e) => update(i, { type: e.target.value as Annotation['type'] })}
            >
              <option value="line">Line</option>
              <option value="band">Band</option>
            </select>
            <select
              className={styles.selectSm}
              value={a.axis}
              onChange={(e) => update(i, { axis: e.target.value as Annotation['axis'] })}
            >
              <option value="y">Y axis</option>
              <option value="x">X axis</option>
            </select>
            <button className={styles.removeBtn} onClick={() => remove(i)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>

          <div className={styles.row}>
            <input
              className={styles.input}
              placeholder="Value"
              value={String(a.value ?? '')}
              onChange={(e) => {
                const num = Number(e.target.value);
                update(i, { value: isNaN(num) ? e.target.value : num });
              }}
              style={{ width: 80 }}
            />
            {a.type === 'band' && (
              <input
                className={styles.input}
                placeholder="Upper"
                value={String(a.value_upper ?? '')}
                onChange={(e) => {
                  const num = Number(e.target.value);
                  update(i, { value_upper: isNaN(num) ? e.target.value : num });
                }}
                style={{ width: 80 }}
              />
            )}
            <input
              className={styles.input}
              placeholder="Label"
              value={a.label}
              onChange={(e) => update(i, { label: e.target.value })}
              style={{ flex: 1 }}
            />
          </div>

          <div className={styles.row}>
            {COLOURS.map((c) => (
              <button
                key={c}
                onClick={() => update(i, { colour: c })}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: c,
                  border: a.colour === c ? '2px solid var(--text)' : '2px solid transparent',
                  cursor: 'pointer',
                  padding: 0,
                }}
                title={c}
              />
            ))}
          </div>
        </div>
      ))}

      <button className={styles.addBtn} onClick={add}>
        <FontAwesomeIcon icon={faPlus} /> Add annotation
      </button>
    </div>
  );
}
